import React, { useState } from 'react';
import {
    Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Collapse, Typography, Divider, alpha, Chip
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';

// Icon mapping for different menu categories
const getIconForMenu = (menuName) => {
    const name = menuName.toLowerCase();
    if (name.includes('dashboard')) return <DashboardIcon />;
    if (name.includes('employee') || name.includes('payroll')) return <PeopleIcon />;
    if (name.includes('department') || name.includes('unit')) return <BusinessIcon />;
    if (name.includes('designation') || name.includes('admin')) return <WorkIcon />;
    if (name.includes('salary') || name.includes('allowance')) return <AttachMoneyIcon />;
    if (name.includes('attendance') || name.includes('leave')) return <AssignmentIcon />;
    if (name.includes('report')) return <DescriptionIcon />;
    if (name.includes('setup') || name.includes('setting')) return <SettingsIcon />;
    return <FiberManualRecordIcon sx={{ fontSize: 12 }} />;
};

export default function ModernSidebar({ onClose }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { navbarMenu } = useSelector((state) => state.menus);
    const [openMenus, setOpenMenus] = useState({});

    const handleMenuClick = (index) => {
        setOpenMenus(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleSubMenuClick = (url) => {
        navigate(url);
        if (onClose) onClose();
    };

    const isActive = (url) => location.pathname === url;

    return (
        <Box sx={{ overflow: 'auto', height: 'calc(100vh - 64px)' }}>
            <List sx={{ p: 1.5 }}>
                {navbarMenu.map((menu, index) => {
                    const hasSubMenus = menu.ArrSubMenu && menu.ArrSubMenu.length > 0;
                    const isExpanded = openMenus[index];

                    return (
                        <React.Fragment key={index}>
                            {/* Main Menu Item */}
                            <ListItem
                                disablePadding
                                sx={{
                                    mb: 0.5,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                }}
                            >
                                <ListItemButton
                                    onClick={() => handleMenuClick(index)}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        px: 2,
                                        background: isExpanded
                                            ? 'linear-gradient(135deg, #1E90FF15 0%, #1c86ee15 100%)'
                                            : 'transparent',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #1E90FF10 0%, #1c86ee10 100%)',
                                            transform: 'translateX(4px)',
                                        },
                                        transition: 'all 0.3s ease',
                                        borderLeft: isExpanded ? '4px solid #1E90FF' : '4px solid transparent',
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 40,
                                            color: isExpanded ? '#1E90FF' : '#666',
                                        }}
                                    >
                                        {getIconForMenu(menu.headMenu)}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={menu.headMenu}
                                        primaryTypographyProps={{
                                            fontWeight: isExpanded ? 700 : 600,
                                            fontSize: '0.95rem',
                                            color: isExpanded ? '#1E90FF' : '#333',
                                        }}
                                    />
                                    {hasSubMenus && (
                                        <>
                                            <Chip
                                                label={menu.ArrSubMenu.length}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    bgcolor: isExpanded ? '#1E90FF' : '#e0e0e0',
                                                    color: isExpanded ? 'white' : '#666',
                                                    mr: 1,
                                                }}
                                            />
                                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </>
                                    )}
                                </ListItemButton>
                            </ListItem>

                            {/* Sub Menu Items */}
                            {hasSubMenus && (
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding sx={{ pl: 1 }}>
                                        {menu.ArrSubMenu.map((subMenu, subIndex) => {
                                            const active = isActive(subMenu.menuURL);
                                            return (
                                                <ListItem
                                                    key={`${index}-${subIndex}`}
                                                    disablePadding
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    <ListItemButton
                                                        onClick={() => handleSubMenuClick(subMenu.menuURL)}
                                                        sx={{
                                                            borderRadius: 2,
                                                            py: 1,
                                                            px: 2,
                                                            pl: 6,
                                                            bgcolor: active ? alpha('#1E90FF', 0.15) : 'transparent',
                                                            borderLeft: active ? '3px solid #1E90FF' : '3px solid transparent',
                                                            '&:hover': {
                                                                bgcolor: alpha('#1E90FF', 0.08),
                                                                transform: 'translateX(4px)',
                                                            },
                                                            transition: 'all 0.3s ease',
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                minWidth: 30,
                                                                color: active ? '#1E90FF' : '#999',
                                                            }}
                                                        >
                                                            <FiberManualRecordIcon sx={{ fontSize: 10 }} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={subMenu.subMenu}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.875rem',
                                                                fontWeight: active ? 700 : 500,
                                                                color: active ? '#1E90FF' : '#555',
                                                            }}
                                                        />
                                                        {active && (
                                                            <Box
                                                                sx={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    bgcolor: '#1E90FF',
                                                                    animation: 'pulse 2s infinite',
                                                                    '@keyframes pulse': {
                                                                        '0%, 100%': {
                                                                            opacity: 1,
                                                                            transform: 'scale(1)',
                                                                        },
                                                                        '50%': {
                                                                            opacity: 0.5,
                                                                            transform: 'scale(1.2)',
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                    <Divider sx={{ my: 1, mx: 2 }} />
                                </Collapse>
                            )}
                        </React.Fragment>
                    );
                })}
            </List>

            {/* Footer Info */}
            <Box
                sx={{
                    p: 2,
                    mt: 2,
                    mx: 2,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1E90FF15 0%, #1c86ee15 100%)',
                    border: '1px solid #1E90FF30',
                }}
            >
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Quick Access
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#1E90FF" gutterBottom>
                    HRMS v2.0
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    © 2025 All Rights Reserved
                </Typography>
            </Box>
        </Box>
    );
}
